import React, { memo, MutableRefObject, useMemo } from 'react';
import { SearchBox } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/SearchBox';
import { Action } from '@reduxjs/toolkit';
import { useExperiments } from '@johnlewispartnership/wtr-website-decisioner-client/express';
import classNames from 'classnames';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import locator from 'utils/locator';
import autoSuggestClear from 'redux/modules/auto-suggest/actions/auto-suggest-clear';
import autoSuggestSearch from 'redux/modules/auto-suggest/actions/auto-suggest-search';
import { updateSearchTerm } from 'redux/modules/search/actions/update-search-term';
import { getAutoSuggestActive } from 'redux/modules/auto-suggest/selectors';
import AutoComplete from 'components/Search/AutoComplete';
import SearchHistory from 'components/Search/SearchHistory';
import MultiSearchModal from 'components/MultiSearch';
import { getCookiePreferences } from 'utils/cookie';
import { getSearchValueFromSearchQuery } from 'utils/searchQuery';
import { EOS_NEW_HEADER, MONETATE_DEFAULT_VARIANT } from 'constants/monetateExperiments';
import { useSearchForm } from './use-search-form';
import styles from './SearchForm.module.scss';

type Props = {
  homepage: boolean;
  id?: string;
  onSearch: (() => Action) | null;
  placeholder: string;
  historyClassName?: string;
};

const SearchForm = memo(
  ({
    homepage = false,
    id = undefined,
    onSearch = null,
    placeholder = 'Search...',
    historyClassName = '',
  }: Props) => {
    const dispatch = useWtrDispatch();
    const autoSuggestActive = useWtrSelector(getAutoSuggestActive);
    const functionalCookieConsent = useMemo(
      () => getCookiePreferences().functionalCookiesAccepted,
      [],
    );

    const initialValue = useMemo(
      () =>
        (!locator.search?.includes('searchType') &&
          getSearchValueFromSearchQuery(locator.search)) ||
        '',
      [],
    );

    const {
      handleChange,
      handleClear,
      handleFocus,
      handleSelectedAutoCompleteItem,
      handleSelectedSearchHistoryItem,
      handleSubmit,
      hasFocus,
      inputRef,
      insideRef,
      showSearchHistory,
      value,
    } = useSearchForm({
      autoSuggestActive,
      eligibleForAutoSuggest: true,
      functionalCookieConsent,
      initialValue,
      onChange: autoSuggestSearch,
      onClear: autoSuggestClear,
      onSubmit: updateSearchTerm,
      onSearch,
    });

    const inputProps = useMemo(
      () => ({
        onFocus: handleFocus,
        ref: inputRef,
      }),
      [handleFocus, inputRef],
    );

    const { getExperimentById } = useExperiments();

    const { variant } = getExperimentById(EOS_NEW_HEADER.experiment);

    const newHeader = variant !== MONETATE_DEFAULT_VARIANT;

    return (
      <SearchBox
        ariaActivedescendant={showSearchHistory ? 'search-history-item' : 'autocomplete-suggestion'}
        ariaAutocomplete="list"
        ariaOwns={showSearchHistory ? 'search-history' : 'autocomplete-listbox'}
        focus={hasFocus}
        formRef={insideRef}
        id={id}
        initialValue={initialValue}
        inputProps={inputProps}
        onChange={handleChange}
        onClearClick={handleClear}
        onSubmit={handleSubmit}
        placeholder={placeholder}
        value={value}
        className={classNames({ [styles.capWidthOnXLScreens]: newHeader })}
      >
        {hasFocus && !showSearchHistory ? (
          <AutoComplete
            homepage={homepage}
            inputRef={inputRef as MutableRefObject<HTMLInputElement>}
            onClear={() => dispatch(autoSuggestClear())}
            onSelectItem={handleSelectedAutoCompleteItem}
          />
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        )}

        {showSearchHistory ? (
          <SearchHistory
            inputRef={inputRef as MutableRefObject<HTMLInputElement>}
            homepage={homepage}
            onSelectTerm={handleSelectedSearchHistoryItem}
            className={historyClassName}
          />
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        )}
        <MultiSearchModal hidden={homepage || value.length > 0} />
      </SearchBox>
    );
  },
);

SearchForm.displayName = 'SearchForm';

export default SearchForm;
